import { db, functions } from "@/main";
import { SharedEmailAddressInfo } from "../models/SharedEmailAddressInfo";
import { getActiveUser, getUserRef, storeUserUpdate } from "./UserRepository";
import { getClientId, loadClient } from "./ClientRepository";
import { generateRandomUuid } from "@/utils/Utils";
import User from "../models/User";
import { hashPassword } from "@/utils/PasswordUtils";

interface JoinSharedEmailAddressData {
  userId: string;
  userNickname: string;
  password: string;
  sharedEmailAddressId: string;
  clientName: string;
  createdBy: string;
}

export async function tryJoinSharedEmailAddress(
  data: JoinSharedEmailAddressData
) {
  const joinSharedEmailAddress = functions.httpsCallable(
    "joinSharedEmailAddress"
  );

  const result = (await joinSharedEmailAddress(data)).data;

  return result.wasSuccessful;
}

export function getSharedEmailAddressInfoRef(sharedEmailAddressId: string) {
  return db.collection("sharedEmailAddresses").doc(sharedEmailAddressId);
}

export async function getSharedEmailAddressInfo(sharedEmailAddressId: string) {
  const result = await getSharedEmailAddressInfoRef(sharedEmailAddressId).get();

  if (!result) {
    return null;
  }

  return result.data() as SharedEmailAddressInfo;
}

export async function removeUserFromSharedEmailAddressInfo(
  sharedEmailAddressId: string,
  userId: string
) {
  const sharedEmailAddressInfo = await getSharedEmailAddressInfo(
    sharedEmailAddressId
  );

  if (!sharedEmailAddressInfo) {
    return;
  }

  const matchingUserInfo = sharedEmailAddressInfo.userInfo[userId];

  if (!matchingUserInfo) {
    return;
  }

  delete sharedEmailAddressInfo.userInfo[userId];

  const remainingUserIds = Object.keys(sharedEmailAddressInfo.userInfo);

  if (remainingUserIds.length === 0) {
    //No users remain, delete shared email address info
    return getSharedEmailAddressInfoRef(sharedEmailAddressId).delete();
  }

  const clientIdOfUser = matchingUserInfo.clientId;

  let doesUserOfClientRemain = false;

  for (const userId of remainingUserIds) {
    const remainingUserInfo = sharedEmailAddressInfo.userInfo[userId];

    if (remainingUserInfo.clientId === clientIdOfUser) {
      doesUserOfClientRemain = true;
      break;
    }
  }

  if (!doesUserOfClientRemain) {
    sharedEmailAddressInfo.clientIds = sharedEmailAddressInfo.clientIds.filter(
      (clientId) => clientId !== clientIdOfUser
    );
  }

  if (
    !doesUserOfClientRemain &&
    sharedEmailAddressInfo.sourceClientId === clientIdOfUser
  ) {
    sharedEmailAddressInfo.sourceClientId =
      sharedEmailAddressInfo.clientIds[0] ?? "";
  }

  if (sharedEmailAddressInfo.sourceUserId === userId) {
    sharedEmailAddressInfo.sourceUserId = "";

    for (const userId of remainingUserIds) {
      const remainingUserInfo = sharedEmailAddressInfo.userInfo[userId];

      if (
        remainingUserInfo.clientId === sharedEmailAddressInfo.sourceClientId
      ) {
        sharedEmailAddressInfo.sourceUserId = remainingUserInfo.userId;
        break;
      }
    }
  }

  return getSharedEmailAddressInfoRef(sharedEmailAddressId).set(
    sharedEmailAddressInfo
  );
}

export async function deleteSharedEmailAddressInfoForUser(
  sharedEmailAddressId: string,
  userId: string
) {
  return Promise.all([
    removeUserFromSharedEmailAddressInfo(sharedEmailAddressId, userId),
    getUserRef(userId).set({ sharedEmailAddressId: null }, { merge: true }),
  ]);
}

export async function saveShareEmailAddressInfo(
  sharedEmailAddressInfo: SharedEmailAddressInfo
) {
  return getSharedEmailAddressInfoRef(sharedEmailAddressInfo.id).set(
    sharedEmailAddressInfo
  );
}

export async function createSharedEmailAddressInfo(
  user: User,
  password: string
) {
  const emailAddress = user.emailAddress;
  const activeUser = getActiveUser();

  if (!emailAddress) {
    throw Error("missing email address");
  }

  const client = await loadClient();

  if (!client) {
    throw Error();
  }

  if (!user.passwordHashHex || !user.salt) {
    throw Error("Please set the user password first and then try again.");
  }

  const { salt, passwordHashHex } = hashPassword(password);

  const shareEmailAddressInfo: SharedEmailAddressInfo = {
    id: generateRandomUuid(),
    emailAddress: emailAddress,
    emailAddressLowerCase: emailAddress.toLowerCase(),
    passwordHashHex: user.passwordHashHex,
    salt: user.salt,
    emailSharingPasswordHashHex: passwordHashHex,
    emailSharingSalt: salt,
    sourceClientId: client.id,
    sourceUserId: user.id,
    clientIds: [client.id],
    
    createdBy: activeUser?.id,
    lastChangedBy: activeUser?.id,
    
    userInfo: {
      [user.id]: {
        clientId: client.id,
        clientName: client?.clientName ?? "",

        userId: user.id,
        userNickname: user.nickname,

        createdBy: activeUser?.id,
      },
    },
  };

  await Promise.all([
    storeUserUpdate(
      user.id,
      {
        sharedEmailAddressId: shareEmailAddressInfo.id
      }
    ),
    saveShareEmailAddressInfo(shareEmailAddressInfo),
  ]);

  return shareEmailAddressInfo;
}

export async function updatePasswordOfSharedEmailAddress(
  sharedEmailAddressId: string,
  password: string
) {
  const { salt, passwordHashHex } = hashPassword(password);

  return getSharedEmailAddressInfoRef(sharedEmailAddressId).update({
    emailSharingPasswordHashHex: passwordHashHex,
    emailSharingSalt: salt,
  });
}

export async function updateHomeClientOfSharedEmailAddress(
  sharedEmailAddressId: string,
  sourceClientId: string,
  sourceUserId: string,
  lastChangedBy: string,
) {

  return getSharedEmailAddressInfoRef(sharedEmailAddressId).update({
    sourceClientId: sourceClientId,
    sourceUserId: sourceUserId,
    lastChangedBy: lastChangedBy,
  });
}

export async function getSharedEmailAddressesOfClient() {
  const clientId = getClientId();

  return (
    await db
      .collection("sharedEmailAddresses")
      .where("sourceClientId", "==", clientId)
      .get()
  ).docs.map((doc) => doc.data() as SharedEmailAddressInfo);
}